import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useAuth } from '@/core/auth/AuthContext';
import { RequestsListService } from '../services/RequestsList.service';
import type { RequestFilters } from '../types/Requests.types';

export function useRequests() {
  const { profile } = useAuth();
  const companyId = profile?.company_id || '';
  const userId = profile?.id || '';
  const [filters, setFilters] = useState<RequestFilters>({ query: '', status: 'all' });

  const query = useQuery({
    queryKey: ['inventory', 'requests', 'list', companyId],
    queryFn: () => RequestsListService.getRequests(companyId, userId),
    enabled: !!companyId && !!userId,
  });

  const stats = useMemo(() => {
    const data = query.data || [];
    return {
      total: data.length,
      pending: data.filter(r => r.statusCode === 'pending' || r.statusCode === 'requested').length,
      approved: data.filter(r => r.statusCode === 'approved').length,
      completed: data.filter(r => r.statusCode === 'completed' || r.statusCode === 'issued').length,
    };
  }, [query.data]);

  const filteredRequests = useMemo(() => {
    const search = filters.query.toLowerCase().trim();
    return (query.data || []).filter(item => {
      const matchesSearch = !search || 
        item.title?.toLowerCase().includes(search) || 
        item.project?.name?.toLowerCase().includes(search) ||
        item.requester?.full_name?.toLowerCase().includes(search) ||
        item.code?.toLowerCase().includes(search);
      const matchesStatus = filters.status === 'all' || item.statusCode === filters.status;
      return matchesSearch && matchesStatus;
    });
  }, [query.data, filters]);

  return {
    requests: filteredRequests,
    stats,
    filters,
    isLoading: query.isLoading,
    setFilters,
  };
}
